/**
 * Hinweis bei eingehendem Anruf (Screen-Pop) — kompakt oben rechts, blockiert die Arbeit nicht.
 * Annehmen → Unterrichtsverwaltung des Kontakts; Ablehnen → nur dieses Fenster schließen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, PhoneIncoming, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { INCOMING_CALLS_EVENT, incomingCallsEnabled } from '@/lib/incomingCalls';

const POLL_MS = 2500;

export default function IncomingCallOverlay() {
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(incomingCallsEnabled);

  const poll = useCallback(async () => {
    // Solange ein Fehler sichtbar ist, den Hinweis nicht wegräumen
    if (error) return;
    try {
      const r = await api.get('/telephony/incoming');
      const next = (r.data?.calls || [])[0] || null;
      setCall((prev) => {
        if (!next) return null;
        if (prev?.id === next.id) return { ...prev, ...next };
        return next;
      });
    } catch {
      /* nicht eingeloggt / keine Berechtigung */
    }
  }, [error]);

  useEffect(() => {
    const onToggle = (event) => {
      const next = !!event.detail;
      setEnabled(next);
      if (!next) setCall(null);
    };
    window.addEventListener(INCOMING_CALLS_EVENT, onToggle);
    return () => window.removeEventListener(INCOMING_CALLS_EVENT, onToggle);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll, enabled]);

  const accept = async () => {
    if (!call) return;
    setBusy(true);
    setError(null);
    const target = call.uv_query
      ? `/unterrichtsverwaltung?${call.uv_query}`
      : '/unterrichtsverwaltung';
    try {
      await api.post(`/telephony/incoming/${call.id}/accept`);
    } catch (err) {
      // 404: Anruf serverseitig schon weggeräumt — Kontakt öffnen bleibt gewollt.
      if (err?.response?.status !== 404) {
        setError('Der Anruf konnte nicht übernommen werden. Bitte erneut versuchen.');
        setBusy(false);
        return;
      }
    }
    navigate(target);
    setCall(null);
    setBusy(false);
  };

  const reject = async () => {
    if (!call) return;
    setBusy(true);
    try {
      await api.post(`/telephony/incoming/${call.id}/reject`);
    } catch { /* ignore */ }
    setError(null);
    setCall(null);
    setBusy(false);
  };

  if (!call) return null;

  const label = call.contact_name || 'Unbekannter Anrufer';
  const role =
    call.person_type === 'guardian' ? 'Zahlungspflichtige/r'
      : call.person_type === 'student' ? 'Teilnehmer/in'
        : call.person_type === 'teacher' ? 'Lehrkraft'
          : 'Kontakt';

  return (
    <div
      className="fixed top-4 right-4 z-[100] w-[min(30vw,20rem)] max-h-[30vh] pointer-events-none"
      data-testid="incoming-call-overlay"
      role="region"
      aria-live="polite"
      aria-labelledby="incoming-call-title"
    >
      <div className="pointer-events-auto rounded-xl bg-white shadow-xl border border-emerald-200 overflow-hidden flex flex-col max-h-[30vh]">
        <div className="bg-emerald-600 text-white px-3 py-2.5 flex items-center gap-2 shrink-0">
          <div className="rounded-full bg-white/20 p-1.5 animate-pulse shrink-0">
            <PhoneIncoming className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-emerald-100 leading-tight">Eingehender Anruf</p>
            <h2 id="incoming-call-title" className="text-sm font-semibold truncate leading-snug">
              {label} ruft an
            </h2>
          </div>
        </div>
        <div className="px-3 py-2 space-y-1 text-xs text-slate-600 overflow-y-auto">
          <p className="truncate">
            <span className="text-slate-400">Nummer:</span>{' '}
            <span className="font-mono text-slate-800">{call.phone}</span>
          </p>
          {call.contact_name && (
            <p className="truncate">
              <span className="text-slate-400">Rolle:</span> {role}
            </p>
          )}
          {!call.contact_name && (
            <p className="text-amber-700 text-[11px] leading-snug">
              Kein Stammdatentreffer — Annehmen öffnet die UV ohne Vorauswahl.
            </p>
          )}
          {error && (
            <p
              className="rounded-md bg-rose-50 border border-rose-200 px-2 py-1.5 text-[11px] text-rose-700"
              data-testid="incoming-call-error"
            >
              {error}
            </p>
          )}
        </div>
        <div className="px-3 pb-3 flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 border-rose-200 text-rose-700 hover:bg-rose-50 text-xs"
            onClick={reject}
            disabled={busy}
            data-testid="incoming-call-reject"
          >
            <PhoneOff className="h-3.5 w-3.5 mr-1.5" /> Ablehnen
          </Button>
          <Button
            size="sm"
            className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            onClick={accept}
            disabled={busy}
            data-testid="incoming-call-accept"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Phone className="h-3.5 w-3.5 mr-1.5" />}
            Annehmen
          </Button>
        </div>
      </div>
    </div>
  );
}
